/**
 * The five star slots every pet card draws.
 *
 * Always five, filled up to the rating. The empty ones are the point: a 4-star
 * pet showing four stars and nothing else could be a 4-star scale or a 4-of-6
 * one, and a player deciding what to chase deserves to know which.
 *
 * The rating itself is never passed around independently of the rarity - it is
 * derived from it in `@kenocyber/shared`, so a card cannot claim four stars
 * while its border paints Perfect.
 */

interface PetStarsProps {
  /** How many are filled. Comes from the rarity, never from a prop guess. */
  readonly stars: number;
  /** How many slots exist. Five today, and the contract pins it. */
  readonly slots: number;
  /** Suffix for the test id, so a spec can address one card's stars. */
  readonly testId: string;
  /** Inline renders in a text row rather than pinned to a cell corner. */
  readonly inline?: boolean;
}

export function PetStars({ stars, slots, testId, inline }: PetStarsProps) {
  return (
    // `role="img"` with a single label: a screen reader should hear "4 of 5
    // stars", not five separate characters.
    <span
      className={inline ? 'pet-stars pet-stars-inline' : 'pet-stars'}
      data-testid={`pet-stars-${testId}`}
      data-filled={stars}
      role="img"
      aria-label={`${stars} de ${slots} estrelas`}
    >
      {/* The slot NUMBER is the key, not a list index: these are fixed
          positions on a scale that never reorders. */}
      {Array.from({ length: slots }, (_, offset) => offset + 1).map((slot) => (
        <span
          key={slot}
          className="pet-star"
          data-filled={slot <= stars ? 'true' : 'false'}
          aria-hidden="true"
        >
          {slot <= stars ? '★' : '☆'}
        </span>
      ))}
    </span>
  );
}
